import {
    Input,
    InputGroup,
    InputProps,
    InputRightElement,
} from '@chakra-ui/react'
import { UseComboboxReturnValue } from 'downshift'
import * as React from 'react'
import { Direction } from '../../../icons/definitions'
import { Chevron } from '../../../icons'
import { useColor } from '../../../theme'
import { IFormValidationProps } from '../hooks'
import { TComboboxOption } from '../types'

type ComboboxInputProps =
    IFormValidationProps & Pick<InputProps, 'placeholder' | 'size' | 'variant'> & {
        controls: UseComboboxReturnValue<TComboboxOption>
        inputValue: string
        setInputValue: React.Dispatch<React.SetStateAction<string>>
        setInnerOptions: React.Dispatch<React.SetStateAction<TComboboxOption[]>>
        options: TComboboxOption[]
        setSelectedOption: React.Dispatch<React.SetStateAction<TComboboxOption | null>>
        onSelect: (option: TComboboxOption | null) => void
        setIsFocused: React.Dispatch<React.SetStateAction<boolean>>
        isDisabled?: boolean
        icon?: React.JSX.Element
        isAuth: boolean
        readonly?: boolean
    }


export const ComboboxInput: React.FC<ComboboxInputProps> = React.forwardRef<HTMLInputElement, ComboboxInputProps>(({
    size, controls, inputValue, setInnerOptions, options, setIsFocused, isInvalid, placeholder, variant, isDisabled, icon, isAuth, readonly
}, ref) => {
    const disabledTextColor = useColor({ nuance: 0, palette: 'text' })
    const errorColor = useColor({ nuance: 'red', palette: 'colors' })
    const iconColor = useColor({ nuance: 6, palette: 'text' })
    const authTextColor = useColor({
        nuance: 6,
        palette: 'text',
    })


    const handleOnFocusAndOnClick = React.useCallback(() => {
        // Make it behave like Google search.
        if (!controls.isOpen) {
            controls.openMenu()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controls.isOpen, controls.openMenu])

    const handleToggleMenu = React.useCallback(() => {
        controls.toggleMenu()

        if (inputValue) {
            setInnerOptions(options)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controls.toggleMenu, inputValue, options])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onFocus = React.useCallback(() => setIsFocused(true), [])


    return (
        <InputGroup size={size}>
            <Input
                _invalid={{
                    borderColor: errorColor,
                    borderWidth: '1px',
                }}
                autoComplete="false"
                backgroundColor="#EDEDF2"
                data-testid="combobox-input"
                fontSize='md'
                isInvalid={isInvalid}
                onFocus={onFocus}
                placeholder={placeholder}
                readOnly={readonly}
                ref={ref}
                variant={variant}
                {...controls.getInputProps({ disabled: isDisabled, onFocus: handleOnFocusAndOnClick, onClick: handleOnFocusAndOnClick })}
                {...(isDisabled ? { color: disabledTextColor } : {})}
            />
            <InputRightElement
                color={isAuth ? authTextColor : iconColor}
                cursor={isDisabled ? 'not-allowed' : 'pointer'}
                data-testid="combobox-input-actions"
                justifyContent="end"
                onClick={handleToggleMenu}
                opacity={isDisabled ? 0.3 : 1}
                paddingRight="1rem">
                {icon ?? <Chevron boxSize={3} direction={controls.isOpen ? Direction.Up : Direction.Down} />}
            </InputRightElement>
        </InputGroup>
    )
})